/**
 * Sprite cache using WeakMap for automatic cleanup
 * Based on pixel-agents spriteCache.ts approach
 */

import type { SpriteData } from "../types/index.js";

// WeakMap per zoom level - sprites auto-cleanup when SpriteData is GC'd
const zoomCaches = new Map<number, WeakMap<SpriteData, HTMLCanvasElement>>();

/**
 * Get a cached canvas for a sprite at a given zoom level
 * Creates the canvas if not cached, using WeakMap for auto-cleanup
 */
export function getCachedSprite(sprite: SpriteData, zoom: number): HTMLCanvasElement {
  let cache = zoomCaches.get(zoom);
  if (!cache) {
    cache = new WeakMap();
    zoomCaches.set(zoom, cache);
  }

  const cached = cache.get(sprite);
  if (cached) return cached;

  const rows = sprite.length;
  const cols = sprite[0]?.length || 0;
  const canvas = document.createElement("canvas");
  canvas.width = cols * zoom;
  canvas.height = rows * zoom;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const color = sprite[r]?.[c];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(c * zoom, r * zoom, zoom, zoom);
    }
  }

  cache.set(sprite, canvas);
  return canvas;
}

/**
 * Generate a 1px white outline SpriteData (2px larger in each dimension)
 */
const outlineCache = new WeakMap<SpriteData, SpriteData>();

export function getOutlineSprite(sprite: SpriteData): SpriteData {
  const cached = outlineCache.get(sprite);
  if (cached) return cached;

  const rows = sprite.length;
  const cols = sprite[0]?.length || 0;

  // Expanded grid: +2 in each dimension for 1px border
  const outline: string[][] = [];
  for (let r = 0; r < rows + 2; r++) {
    outline.push(new Array<string>(cols + 2).fill(""));
  }

  // For each opaque pixel, mark its 4 cardinal neighbors as white
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (sprite[r][c] === "") continue;
      const er = r + 1;
      const ec = c + 1;
      if (outline[er - 1][ec] === "") outline[er - 1][ec] = "#FFFFFF";
      if (outline[er + 1][ec] === "") outline[er + 1][ec] = "#FFFFFF";
      if (outline[er][ec - 1] === "") outline[er][ec - 1] = "#FFFFFF";
      if (outline[er][ec + 1] === "") outline[er][ec + 1] = "#FFFFFF";
    }
  }

  // Clear pixels that overlap with original opaque pixels
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (sprite[r][c] !== "") {
        outline[r + 1][c + 1] = "";
      }
    }
  }

  outlineCache.set(sprite, outline);
  return outline;
}
